/**
 * Re-open a completed task (step-model E5, spec 2026-09-20 §6.3, D59): the
 * step opens again as a NEW task pre-filled with the current values; the
 * completed task stays in the record's history as it was. A reason is
 * required (1–2000 characters once trimmed — the API's own rule).
 */
import { useState } from 'react'
import {
  Button, Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle, Textarea,
} from '@/components/ui'

const MAX_REASON = 2000

export function ReopenTaskDialog({
  stepTitle,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  stepTitle: string
  busy: boolean
  /** Already worded (`processErrorMessage`); shown under the reason. */
  error: string | null
  onConfirm: (reason: string) => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  const ready = trimmed.length > 0 && trimmed.length <= MAX_REASON && !busy
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="sm" showClose={false}>
        <ModalHeader>
          <ModalTitle>Re-open “{stepTitle}”</ModalTitle>
          <ModalDescription>
            The step opens again as a new task with the current values filled in. This completed task stays in the
            record’s history. Values from later steps are kept and marked as from before the re-open.
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-2 text-sm">
          <label className="block space-y-1 font-medium text-foreground">
            <span>Why is this step being re-opened?</span>
            <Textarea aria-label="Reason for re-opening" rows={3} maxLength={MAX_REASON} value={reason}
              onChange={(e) => setReason(e.target.value)} />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!ready} onClick={() => onConfirm(trimmed)}>
            Re-open step
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
